import { useState, useEffect, useCallback, useRef } from 'react';
import type { GameState } from './types';
import { UPGRADE_DEFS, ERAS } from './constants';

const TICK_MS = 100;
const STORAGE_KEY = 'evolving-world-save';
const MAX_OFFLINE_MS = 8 * 60 * 60 * 1000;
const SAVE_VERSION = 1;
const SAVE_INTERVAL_MS = 5000;

const INITIAL_STATE: GameState = {
  saveVersion: SAVE_VERSION,
  energy: 0,
  totalEnergyEarned: 0,
  currentEraIndex: 0,
  ownedUpgrades: {},
  clickCount: 0,
};

function migrateState(raw: Partial<GameState>): Partial<GameState> {
  const version = raw.saveVersion ?? 0;
  let state = { ...raw };
  // v0 → v1: no structural changes, just stamp version
  if (version < 1) {
    state = { ...state, saveVersion: 1 };
  }
  // Future: if (version < 2) { ... }
  return state;
}

export function getUpgradeCost(upgradeId: string, owned: number): number {
  const def = UPGRADE_DEFS.find((u) => u.id === upgradeId);
  if (!def) return Infinity;
  return Math.ceil(def.baseCost * Math.pow(1.15, owned));
}

// Total cost to buy `count` units starting from `owned` (geometric series)
export function getBulkUpgradeCost(upgradeId: string, owned: number, count: number): number {
  if (count <= 0) return 0;
  const def = UPGRADE_DEFS.find((u) => u.id === upgradeId);
  if (!def) return Infinity;
  return Math.ceil(def.baseCost * (Math.pow(1.15, owned + count) - Math.pow(1.15, owned)) / 0.15);
}

// Largest n such that buying n units from `owned` costs ≤ energy
export function getMaxAffordableCount(upgradeId: string, owned: number, energy: number): number {
  const def = UPGRADE_DEFS.find((u) => u.id === upgradeId);
  if (!def || energy <= 0) return 0;
  const val = (energy * 0.15) / def.baseCost + Math.pow(1.15, owned);
  return Math.max(0, Math.floor(Math.log(val) / Math.log(1.15) - owned));
}

export function getTotalProduction(ownedUpgrades: Record<string, number>): number {
  return UPGRADE_DEFS.reduce((total, def) => {
    return total + def.baseProduction * (ownedUpgrades[def.id] ?? 0);
  }, 0);
}

export function calcEraIndex(totalEarned: number): number {
  let idx = 0;
  for (let i = 0; i < ERAS.length; i++) {
    const era = ERAS[i];
    if (era && totalEarned >= era.requiredEnergy) idx = i;
  }
  return idx;
}

function loadState(): { state: GameState; offlineGained: number } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<GameState>;
      const migrated = migrateState(parsed);
      const base = { ...INITIAL_STATE, ...migrated };
      const elapsedMs = Date.now() - (parsed.lastSaved ?? Date.now());
      const clampedMs = Math.min(elapsedMs, MAX_OFFLINE_MS);
      const gained = getTotalProduction(base.ownedUpgrades) * (clampedMs / 1000);
      if (gained > 0) {
        const newTotal = base.totalEnergyEarned + gained;
        return {
          state: {
            ...base,
            energy: base.energy + gained,
            totalEnergyEarned: newTotal,
            currentEraIndex: calcEraIndex(newTotal),
          },
          offlineGained: gained,
        };
      }
      return { state: base, offlineGained: 0 };
    }
  } catch {
    // ignore parse errors — start fresh
  }
  return { state: INITIAL_STATE, offlineGained: 0 };
}

export function useGameState() {
  const [{ state: initialState, offlineGained }] = useState(loadState);
  const [state, setState] = useState<GameState>(initialState);
  const [offlinePopup, setOfflinePopup] = useState<number>(offlineGained);

  // Keep a ref in sync for reading inside intervals without stale closures
  const stateRef = useRef(state);
  stateRef.current = state;

  // Tracks when the tab was hidden so elapsed time can be credited on resume
  const hiddenAtRef = useRef<number | null>(null);

  // Auto-production tick — paused while document is hidden; elapsed time credited on resume
  useEffect(() => {
    let id: ReturnType<typeof setInterval> | null = null;

    const tickBody = () => {
      const production = getTotalProduction(stateRef.current.ownedUpgrades);
      if (production === 0) return;
      const earned = production * (TICK_MS / 1000);
      setState((prev) => {
        const newTotal = prev.totalEnergyEarned + earned;
        return {
          ...prev,
          energy: prev.energy + earned,
          totalEnergyEarned: newTotal,
          currentEraIndex: calcEraIndex(newTotal),
        };
      });
    };

    const start = () => {
      if (id != null) return;
      id = setInterval(tickBody, TICK_MS);
    };

    const stop = () => {
      if (id == null) return;
      clearInterval(id);
      id = null;
      hiddenAtRef.current = Date.now();
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...stateRef.current, lastSaved: Date.now() }));
      } catch {}
    };

    const creditElapsed = () => {
      const hiddenAt = hiddenAtRef.current;
      if (hiddenAt == null) return;
      hiddenAtRef.current = null;
      const elapsed = Date.now() - hiddenAt;
      const clampedMs = Math.min(elapsed, MAX_OFFLINE_MS);
      const production = getTotalProduction(stateRef.current.ownedUpgrades);
      const gained = production * (clampedMs / 1000);
      if (gained <= 0) return;
      setState((prev) => {
        const newTotal = prev.totalEnergyEarned + gained;
        return {
          ...prev,
          energy: prev.energy + gained,
          totalEnergyEarned: newTotal,
          currentEraIndex: calcEraIndex(newTotal),
        };
      });
    };

    const onVis = () => {
      if (document.hidden) {
        stop();
      } else {
        creditElapsed();
        start();
      }
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Persist to localStorage on a fixed interval (throttled to avoid thrashing)
  useEffect(() => {
    const id = setInterval(() => {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ ...stateRef.current, lastSaved: Date.now() }),
        );
      } catch {
        // ignore — storage quota exceeded or unavailable
      }
    }, SAVE_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Save immediately when the tab is closed to avoid losing the debounce window
  useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...stateRef.current, lastSaved: Date.now() }));
      } catch {
        // ignore — storage quota exceeded or unavailable
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleClick = useCallback(() => {
    setState((prev) => {
      const production = getTotalProduction(prev.ownedUpgrades);
      // Click power scales with 1% of current production (minimum 1)
      const power = Math.max(1, Math.floor(production * 0.01) + 1);
      const newTotal = prev.totalEnergyEarned + power;
      return {
        ...prev,
        energy: prev.energy + power,
        totalEnergyEarned: newTotal,
        currentEraIndex: calcEraIndex(newTotal),
        clickCount: prev.clickCount + 1,
      };
    });
  }, []);

  const buyUpgrade = useCallback((upgradeId: string) => {
    setState((prev) => {
      const owned = prev.ownedUpgrades[upgradeId] ?? 0;
      const cost = getUpgradeCost(upgradeId, owned);
      if (prev.energy < cost) return prev;
      return {
        ...prev,
        energy: prev.energy - cost,
        ownedUpgrades: { ...prev.ownedUpgrades, [upgradeId]: owned + 1 },
      };
    });
  }, []);

  const buyUpgradeBulk = useCallback((upgradeId: string, qty: number | 'max') => {
    setState((prev) => {
      const owned = prev.ownedUpgrades[upgradeId] ?? 0;
      const count =
        qty === 'max'
          ? getMaxAffordableCount(upgradeId, owned, prev.energy)
          : Math.min(qty, getMaxAffordableCount(upgradeId, owned, prev.energy));
      if (count <= 0) return prev;
      const cost = getBulkUpgradeCost(upgradeId, owned, count);
      if (prev.energy < cost) return prev;
      return {
        ...prev,
        energy: prev.energy - cost,
        ownedUpgrades: { ...prev.ownedUpgrades, [upgradeId]: owned + count },
      };
    });
  }, []);

  const resetGame = useCallback(() => {
    setState(INITIAL_STATE);
    setOfflinePopup(0);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore — storage unavailable
    }
  }, []);

  const energyPerSecond = getTotalProduction(state.ownedUpgrades);
  const energyPerClick = Math.max(1, Math.floor(energyPerSecond * 0.01) + 1);

  return {
    state,
    energyPerSecond,
    energyPerClick,
    offlineGained,
    offlinePopup,
    setOfflinePopup,
    handleClick,
    buyUpgrade,
    buyUpgradeBulk,
    resetGame,
    getUpgradeCost: (id: string) => getUpgradeCost(id, state.ownedUpgrades[id] ?? 0),
  };
}
